import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Modal,
  Select,
  Text,
  Tooltip,
  Checkbox,
  Radio,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconGitMerge } from "@tabler/icons";
import { MantineReactTable } from "mantine-react-table";
import { getCounterId, isCounterAdmin } from "pages/Auth/permissionGuard";
import { counters_api } from "pages/master/counter/counter.service";
import { getAlteredCounters } from "pages/master/customers/helperFunctions";
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { api_filter } from "services/master.service";
import { showErrorToast, showSuccessToast } from "utilities/Toast";
import {
  getCustomId,
  getFilterPayload,
  getBodyContent,
} from "services/filterHelperFunctions";

import { stock_apis } from "pages/Inventory/stock-transfer/stock-transfer.service";
import AppBreadcrumb from "components/AppBreadcrumb";
import moment from "moment";
import { httpClient } from "lib/axios";

export default function MergeBatches() {
  const [countersList, setCountersList] = useState([]);
  const [counter, setCounter] = useState(isCounterAdmin() ? getCounterId() : "");
  let isMobileScreen = useMediaQuery("(max-width: 768px)");
  const [columnFilters, setColumnFilters] = useState([]);
  const [modalBatches, setModalBatches] = useState({ title: "", product_id: null, batch: [] });
  const [productSelectionModal, setProductSelectionModal] = useState(false);
  const [apiModel, setApiModel] = useState("Products");

  // Merge selections
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [columnFilters]);

  useQuery({
    queryKey: ["counters-data-list"],
    queryFn: () => counters_api.get(),
    onSuccess: (data) => {
      setCountersList(
        getAlteredCounters(data.data).filter((c) => c.label.toLowerCase() !== "warehouse")
      );
    },
  });

  const getFilteredData = async (forCsv = false) => {
    const model = apiModel === "Products" ? "Products" : "CounterStock";
    const serializer = apiModel === "Products" ? "ProductStockSerializer" : "CounterStockSerializer";

    const appendData = {
      "Products": [
        { id: "product_code", append: "", replace_id: "product_code" },
        { id: "title", append: "", replace_id: "title" },
        { id: "stock_qty", append: "", replace_id: "stock_qty" },
      ],
      "CounterStock": [
        { id: "product_code", append: "product__", replace_id: "product_code" },
        { id: "title", append: "product__", replace_id: "title" },
        { id: "stock_qty", append: "product__", replace_id: "stock_qty" },
      ],
    };

    const custom = getCustomId(columns, columnFilters, appendData?.[apiModel]);
    const filterPayload = getFilterPayload(custom?.columns, custom?.columnFilters);
    const counterId = counter;
    const fields = ["product_code", "title", "mrp", "selling_price", "stock_qty", "has_pending_merge"];

    try {
      const res = await api_filter(
        model,
        filterPayload,
        fields,
        serializer,
        counterId,
        pagination.pageIndex + 1,
        pagination.pageSize,
        forCsv
      );

      if (res.success) {
        return {
          data: res.data,
          count: res.count
        };
      } else {
        showErrorToast({ title: "Error", message: res.message });
        return { data: [], count: 0 };
      }
    } catch (e) {
      showErrorToast({ title: "Error", message: e.message });
      return { data: [], count: 0 };
    }
  };

  const stock_data = useQuery({
    queryKey: [
      "stock-list-data-merge",
      pagination.pageIndex,
      pagination.pageSize,
      counter,
      columnFilters,
    ],
    queryFn: async () => {
      if (columnFilters.length > 0) {
        return getFilteredData(false);
      } else {
        return stock_apis.get_stock_list_for_counter_view(
          counter,
          pagination.pageIndex + 1,
          pagination.pageSize
        );
      }
    },
    keepPreviousData: true,
  });

  const getExpiryDayLeft = (expiry_date) => {
    if (!expiry_date) return "-";
    const expirationDate = new Date(expiry_date);
    const currentDate = new Date();
    const timeDifference = expirationDate.getTime() - currentDate.getTime();
    const days = Math.ceil(timeDifference / (1000 * 3600 * 24));
    return days;
  };

  const getNearestExpiryBatch = (batches = []) => {
    if (!batches.length) return null;
    return batches
      .filter((b) => b.exp_date)
      .sort((a, b) => new Date(a.exp_date) - new Date(b.exp_date))[0];
  };

  const columns = useMemo(
    () => [
      {
        header: "Product Code",
        accessorKey: "product_code",
        size: 110,
        filterVariant: "range",
      },
      {
        header: "Product Name",
        accessorKey: "title",
        size: 280,
        Cell: ({ row }) => (
          <Box sx={{ lineHeight: 1.1 }}>
            <Text fw={700} size="sm">{row.original.title}</Text>
            <Text size="10px" color="dimmed" sx={{ marginTop: -2 }}>{row.original.product_code}</Text>
          </Box>
        ),
      },
      {
        header: "Pricing",
        id: "pricing",
        size: 130,
        Cell: ({ row }) => {
          const model = apiModel === "Products" ? "batch" : "counter_batch";
          const nearestBatch = getNearestExpiryBatch(row.original?.[model]);
          const mrp = nearestBatch ? nearestBatch.mrp : row.original.mrp;
          const price = nearestBatch ? nearestBatch.selling_price : row.original.selling_price;
          return (
            <Box sx={{ lineHeight: 1.1 }}>
              <Text size="9px" fw={800} color="dimmed" tt="uppercase">MRP: ₹{mrp}</Text>
              <Text size="xs" fw={700} color="brand">Price: ₹{price}</Text>
            </Box>
          );
        },
      },
      {
        header: "Current Stock",
        accessorKey: "stock_qty",
        size: 130,
        filterVariant: "range",
        Cell: ({ cell }) => {
          const val = Number(cell.getValue()) || 0;
          let color = "gray";
          if (val === 0) color = "dark";
          else if (val > 0 && val < 10) color = "red";
          else if (val >= 10 && val < 50) color = "yellow";
          else if (val >= 50) color = "green";

          return (
            <Badge
              variant="filled"
              color={color}
              size="md"
              radius="sm"
              sx={{ fontWeight: 800, letterSpacing: 0.5, height: 20 }}
            >
              {val.toFixed(3)}
            </Badge>
          );
        },
      },
      {
        header: "Total Value",
        size: 120,
        Cell: ({ row }) => (
          <Box sx={{
            background: 'rgba(34, 139, 230, 0.08)',
            padding: '2px 8px',
            borderRadius: '4px',
            textAlign: 'center',
            lineHeight: 1
          }}>
            <Text size="8px" color="blue" fw={800}>VALUE</Text>
            <Text fw={800} size="xs" color="blue">
              ₹{(row.original.selling_price * row.original.stock_qty).toFixed(2)}
            </Text>
          </Box>
        ),
      },
      {
  header: "Available Batches",
  id: "available_batches",

  // IMPORTANT
  accessorFn: (row) => {
    const batches = row?.counter_batch || row?.batch || [];
    return batches.filter((b) => Number(b.stock) > 0).length;
  },

  size: 160,

  Cell: ({ cell }) => {
    const count = cell.getValue();

    return (
      <Box sx={{ textAlign: "center" }}>
        <Badge
          size="lg"
          variant="outline"
          color={count > 1 ? "blue" : "gray"}
          styles={{
            root: {
              borderStyle: count > 1 ? "solid" : "dashed",
              height: 24,
            },
          }}
        >
          {count} {count === 1 ? "BATCH" : "BATCHES"}
        </Badge>
      </Box>
    );
  },
},
      {
        header: "Actions",
        size: 100,
        Cell: ({ row }) => {
          const batches =
            row.original?.counter_batch ||
            row.original?.batch ||
            row.original?.product?.batch ||
            [];
          const isPending = row.original.has_pending_merge;
          return (
            <div>
              <Tooltip label={isPending ? "Merge request pending approval" : "Open Merge Interface"}>
                <Button
                  variant="light"
                  color={isPending ? "orange" : "blue"}
                  leftIcon={<IconGitMerge size={16} />}
                  size="xs"
                  radius="md"
                  disabled={isPending}
                  onClick={() => {
                    if (!counter) {
                      showErrorToast({
                        title: "Error",
                        message: "Please select a counter first.",
                      });
                      return;
                    }
                    setProductSelectionModal(true);
                    setModalBatches({
                      title: row.original?.title,
                      product_id: row.original?.product || row.original?.id,
                      batch: batches,
                    });
                    setSelectedSources([]);
                    setSelectedTarget(null);
                  }}
                >
                  {isPending ? "Pending" : "Merge"}
                </Button>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [apiModel, counter]
  );

  useEffect(() => {
    if (counter) {
      setApiModel("CounterStock");
    } else {
      setApiModel("Products");
    }
  }, [counter]);

  // Toggle source batch selection
  const handleSourceToggle = (batchId, checked, maxQty) => {
    setSelectedSources((prev) => {
      if (checked) {
        return [...prev, { batch_id: batchId, qty: maxQty }];
      } else {
        return prev.filter((item) => item.batch_id !== batchId);
      }
    });
    if (selectedTarget === batchId) {
      setSelectedTarget(null);
    }
  };

  const submitMergeRequest = async () => {
    if (selectedSources.length === 0) {
      showErrorToast({ title: "Error", message: "Please select at least one source batch." });
      return;
    }
    if (!selectedTarget) {
      showErrorToast({ title: "Error", message: "Please select a target batch." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        counter_id: counter,
        product_id: modalBatches.product_id,
        target_batch: selectedTarget,
        source_batches: selectedSources,
      };
      const res = await httpClient.post(`batch_merge/`, payload);

      if (res.data.success) {
        showSuccessToast({ title: "Success", message: "Merge request submitted successfully." });
        setProductSelectionModal(false);
        stock_data.refetch();
      } else {
        showErrorToast({ title: "Error", message: res.data.message });
      }
    } catch (e) {
      showErrorToast({ title: "Error", message: "Failed to submit request." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isMobileScreen ? (
        <Select
          p={5}
          label="Counter"
          defaultValue={isCounterAdmin() ? counter : null}
          placeholder="Select Counter"
          data={countersList || []}
          onChange={setCounter}
          clearable
          searchable
          nothingFound="No counter found"
          disabled={isCounterAdmin()}
        />
      ) : (
        <Container fluid>
          <Box p={25}>
            <Flex justify={"space-between"} align="end">
              <Box>
                <Text size={"lg"} fw={600} mb={10}>
                  Merge Batches Counter
                </Text>
                <AppBreadcrumb
                  crumbs={[
                    { text: "Master" },
                    { text: "Products" },
                    { text: "Merge Batches Counter" },
                  ]}
                />
              </Box>

              <Flex align={"flex-end"} gap={10}>
                <Select
                  label="Counter"
                  defaultValue={isCounterAdmin() ? counter : null}
                  placeholder="Select Counter"
                  data={countersList || []}
                  onChange={setCounter}
                  clearable
                  searchable
                  nothingFound="No counter found"
                  disabled={isCounterAdmin()}
                />
              </Flex>
            </Flex>
          </Box>
        </Container>
      )}

      <Container fluid mt={10} mb={30}>
        <Box px={isMobileScreen ? 5 : 25} sx={{ borderRadius: 2 }}>
          <Box className="datatable-wrapper">
            <MantineReactTable
              manualFiltering={true}
              onColumnFiltersChange={setColumnFilters}
              enableRowVirtualization
              mantineTableContainerProps={{ sx: { maxHeight: "600px" } }}
              state={{
                columnFilters,
                pagination,
                isLoading: stock_data.isLoading || stock_data.isRefetching,
              }}
              mantineTableProps={{ className: "datatable", striped: true }}
              mantinePaginationProps={{
                rowsPerPageOptions: [
                  "10",
                  "15",
                  "20",
                  "25",
                  "30",
                  "50",
                  "100",
                  "500",
                  "1000",
                ],
              }}
              mantineTableCellProps={{
                sx: {
                  padding: '4px 10px !important',
                  height: '48px'
                }
              }}
              initialState={{
                density: "xs",
                columnVisibility: { product_code: true } // Ensure visibility defaults
              }}
              columns={columns}
              layoutMode="grid"
              enableColumnOrdering
              enableColumnResizing
              columnResizeMode="onChange"
              enableHiding
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnActions={false}
              data={stock_data.data?.data || []}
              enableSorting
              enableStickyHeader
              enableGlobalFilter={false}
              onPaginationChange={(updater) => {
                setPagination((prev) => {
                  const newPagination =
                    typeof updater === "function" ? updater(prev) : updater;
                  return { ...prev, ...newPagination };
                });
              }}
              rowCount={stock_data.data?.count}
              manualPagination
            />
          </Box>
        </Box>
      </Container>

      {/* ─── Merge Modal ─── */}
      <Modal
        opened={productSelectionModal}
        onClose={() => setProductSelectionModal(false)}
        title={
          <Box px={10}>
            <Text fw={700} size="md">
              {modalBatches?.title}
            </Text>
            <Text size="xs" color="dimmed" mt={2}>
              Select source batches and a merge target
            </Text>
          </Box>
        }
        size={"820px"}
        centered
      >
        <Box px={10} pb={16}>
          <Divider mb={16} />

          {/* Legend */}
          <Flex gap={20} mb={14} px={2}>
            <Flex align="center" gap={6}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  background: "#e8f5e9",
                  border: "1.5px solid #43a047",
                }}
              />
              <Text size="xs" color="dimmed">
                Merge From (source)
              </Text>
            </Flex>
            <Flex align="center" gap={6}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  background: "#e3f2fd",
                  border: "1.5px solid #1e88e5",
                }}
              />
              <Text size="xs" color="dimmed">
                Merge To (target)
              </Text>
            </Flex>
          </Flex>

          {/* Batch Cards */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {modalBatches?.batch &&
              modalBatches.batch.map((e) => {
                const isSource = selectedSources.some((s) => s.batch_id === e.id);
                const isTarget = selectedTarget === e.id;
                const hasAnyTarget = selectedTarget !== null;

                // Row color based on state
                let rowBg = "#fafafa";
                let rowBorder = "1.5px solid #e9ecef";
                if (isTarget) {
                  rowBg = "#e3f2fd";
                  rowBorder = "1.5px solid #1e88e5";
                } else if (isSource) {
                  rowBg = "#e8f5e9";
                  rowBorder = "1.5px solid #43a047";
                }

                return (
                  <Box
                    key={e.id || e.batch_no}
                    sx={{
                      background: rowBg,
                      border: rowBorder,
                      borderRadius: 10,
                      padding: "12px 16px",
                      transition: "all 0.18s ease",
                    }}
                  >
                    <Flex align="center" justify="space-between" wrap="wrap" gap={8}>
                      {/* Batch Info */}
                      <Flex direction="column" gap={2} sx={{ minWidth: 120 }}>
                        <Text
                          fw={700}
                          size="sm"
                          color={isTarget ? "#1565c0" : isSource ? "#2e7d32" : "dark"}
                        >
                          {e.batch_no}
                        </Text>
                        <Text size="xs" color="dimmed">
                          Exp: {moment(e.exp_date).format("DD-MM-YYYY")}
                        </Text>
                        <Text size="xs" color="blue" fw={500}>
                          Days left: {getExpiryDayLeft(e.exp_date)}
                        </Text>
                      </Flex>

                      {/* Stock & Price */}
                      <Flex gap={24} align="center">
                        <Box sx={{ textAlign: "center" }}>
                          <Text size="xs" color="dimmed">
                            Stock
                          </Text>
                          <Badge
                            size="sm"
                            variant="light"
                            color={e.stock <= 0 ? "red" : e.stock < 10 ? "orange" : "green"}
                            fw={700}
                          >
                            {e.stock}
                          </Badge>
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Text size="xs" color="dimmed">
                            Price
                          </Text>
                          <Text size="sm" fw={600}>
                            ₹{e.selling_price}
                          </Text>
                        </Box>
                      </Flex>

                      {/* Selection Controls */}
                      <Flex align="center" gap={28}>
                        {/* Merge From Checkbox */}
                        <Flex direction="column" align="center" gap={4}>
                          <Text size="xs" color="dimmed" fw={500}>
                            Merge From
                          </Text>
                          <Checkbox
                            checked={isSource}
                            onChange={(event) =>
                              handleSourceToggle(
                                e.id,
                                event.currentTarget.checked,
                                e.stock
                              )
                            }
                            disabled={isTarget || e.stock <= 0}
                            color="green"
                            size="md"
                            styles={{
                              input: {
                                cursor:
                                  isTarget || e.stock <= 0 ? "not-allowed" : "pointer",
                                borderRadius: 5,
                              },
                            }}
                          />
                        </Flex>

                        {/* Merge To Radio — click selected again to deselect */}
                        <Flex direction="column" align="center" gap={4}>
                          <Text size="xs" color="dimmed" fw={500}>
                            Merge To
                          </Text>
                          <Radio
                            checked={isTarget}
                            onClick={() => {
                              if (isTarget) {
                                // Deselect when clicked again
                                setSelectedTarget(null);
                              } else {
                                // Select this as target and remove it from sources
                                setSelectedTarget(e.id);
                                setSelectedSources((prev) =>
                                  prev.filter((item) => item.batch_id !== e.id)
                                );
                              }
                            }}
                            onChange={() => { }}
                            disabled={isSource || (hasAnyTarget && !isTarget)}
                            color="blue"
                            size="md"
                            styles={{
                              radio: {
                                cursor:
                                  isSource || (hasAnyTarget && !isTarget)
                                    ? "not-allowed"
                                    : "pointer",
                              },
                            }}
                          />
                        </Flex>
                      </Flex>
                    </Flex>
                  </Box>
                );
              })}
          </Box>

          {/* Visual Transfer Flow Summary */}
          {(selectedSources.length > 0 || selectedTarget) && (
            <Box
              mt={20}
              p={16}
              sx={{
                background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)',
                borderRadius: 12,
                border: "1px solid #dee2e6",
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}
            >
              <Flex align="center" justify="space-between" wrap="wrap" gap={20}>
                {/* Sources Column */}
                <Box sx={{ flex: 1, minWidth: 150 }}>
                  <Text size="10px" color="dimmed" fw={800} mb={8} tt="uppercase" ls={0.8}>
                    FROM SOURCES ({selectedSources.length})
                  </Text>
                  <Flex gap={6} wrap="wrap">
                    {selectedSources.map((s) => {
                      const b = modalBatches.batch.find((x) => x.id === s.batch_id);
                      return (
                        <Badge
                          key={s.batch_id}
                          size="md"
                          color="green"
                          variant="filled"
                          sx={{ height: 24, fontWeight: 700, borderRadius: 6 }}
                        >
                          {b?.batch_no} • {Number(s.qty).toFixed(2)}
                        </Badge>
                      );
                    })}
                  </Flex>
                </Box>

                {/* Flow Divider */}
                <Flex direction="column" align="center" sx={{ color: '#228be6' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: '#e7f5ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #a5d8ff'
                    }}
                  >
                    <IconGitMerge size={20} />
                  </Box>
                </Flex>

                {/* Target Column Impact */}
                <Box sx={{ flex: 1, minWidth: 200, textAlign: 'right' }}>
                  <Text size="10px" color="dimmed" fw={800} mb={8} tt="uppercase" ls={0.8}>
                    TARGET IMPACT
                  </Text>
                  {selectedTarget ? (
                    <Box>
                      <Badge
                        size="md"
                        color="blue"
                        variant="filled"
                        sx={{ height: 24, fontWeight: 700, borderRadius: 6, marginBottom: 8 }}
                      >
                        {modalBatches.batch.find((x) => x.id === selectedTarget)?.batch_no}
                      </Badge>

                      <Flex direction="column" gap={2}>
                        <Flex justify="flex-end" gap={8}>
                          <Text size="xs" color="dimmed">Current Stock:</Text>
                          <Text size="xs" fw={700}>
                            {Number(modalBatches.batch.find((x) => x.id === selectedTarget)?.stock).toFixed(3)}
                          </Text>
                        </Flex>
                        <Flex justify="flex-end" gap={8}>
                          <Text size="xs" color="green" fw={600}>Merging In:</Text>
                          <Text size="xs" fw={700} color="green">
                            +{selectedSources.reduce((acc, curr) => acc + Number(curr.qty), 0).toFixed(3)}
                          </Text>
                        </Flex>
                        <Divider my={4} sx={{ width: 120, marginLeft: 'auto', borderStyle: 'dashed' }} />
                        <Flex justify="flex-end" gap={8} align="center">
                          <Text size="xs" fw={800} color="blue">NEW TOTAL:</Text>
                          <Text size="md" fw={900} color="blue" sx={{ lineHeight: 1 }}>
                            {(
                              Number(modalBatches.batch.find((x) => x.id === selectedTarget)?.stock) +
                              selectedSources.reduce((acc, curr) => acc + Number(curr.qty), 0)
                            ).toFixed(3)}
                          </Text>
                        </Flex>
                      </Flex>
                    </Box>
                  ) : (
                    <Text size="xs" color="red" fw={600} italic>Select target batch</Text>
                  )}
                </Box>
              </Flex>
            </Box>
          )}

          {/* Footer Buttons */}
          <Flex justify="flex-end" mt={20} gap={10}>
            <Button
              variant="subtle"
              color="gray"
              onClick={() => {
                setSelectedSources([]);
                setSelectedTarget(null);
              }}
              disabled={selectedSources.length === 0 && !selectedTarget}
            >
              Clear
            </Button>
            <Button
              loading={isSubmitting}
              onClick={submitMergeRequest}
              disabled={selectedSources.length === 0 || !selectedTarget}
              sx={{ minWidth: 140 }}
            >
              Submit Request
            </Button>
          </Flex>
        </Box>
      </Modal>
    </>
  );
}
